import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import { exportDeadStockCsv, fetchDeadStock, type DeadStockItem } from './api';
import { formatUnits } from './figures';

const { Text } = Typography;

export interface DeadStockDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** The parts in stock with no sale in 180 days, one row per part, biggest value first. */
export function DeadStockDrawer({ open, onClose }: DeadStockDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { data, error, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['analytics', 'parts', 'dead-stock'],
    queryFn: ({ pageParam }) => fetchDeadStock(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: open,
  });

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  const download = async (): Promise<void> => {
    try {
      const csv = await exportDeadStockCsv();
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dead-stock.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('insights.parts.deadStock.downloadFailed'));
    }
  };

  const columns: TableProps<DeadStockItem>['columns'] = [
    {
      title: t('insights.parts.deadStock.sku'),
      key: 'sku',
      render: (_: unknown, r) => (
        <Flex vertical>
          <Text className="tabular">{r.skuCode}</Text>
          <Text type="secondary">{r.name}</Text>
        </Flex>
      ),
    },
    {
      title: t('insights.parts.deadStock.qty'),
      key: 'qty',
      align: 'right',
      render: (_: unknown, r) => <span className="tabular">{formatUnits(r.qty)}</span>,
    },
    {
      title: t('insights.parts.deadStock.value'),
      key: 'value',
      align: 'right',
      render: (_: unknown, r) => formatMoney(r.value),
    },
    {
      title: t('insights.parts.deadStock.lastSold'),
      key: 'lastSold',
      render: (_: unknown, r) =>
        r.lastSoldOn === null ? (
          t('insights.parts.deadStock.neverSince')
        ) : (
          <span className="tabular">{dayjs(r.lastSoldOn).format('D MMM YYYY')}</span>
        ),
    },
  ];

  return (
    <Drawer
      open={open}
      title={t('insights.parts.deadTitle')}
      onClose={onClose}
      width={960}
      className="nks-action-drawer"
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            void download();
          }}
        >
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {error !== null && (
          <Text type="danger">
            {error instanceof ApiError ? error.message : t('insights.parts.deadStock.loadFailed')}
          </Text>
        )}
        <Table<DeadStockItem>
          rowKey="skuCode"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={open && isPending}
          locale={{ emptyText: t('insights.parts.deadStock.empty') }}
          dataSource={rows}
          columns={columns}
        />
        {hasNextPage && (
          <Button
            block
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('insights.parts.deadStock.loadMore')}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}
